import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Minus, Plus, MessageCircle, Truck, ShieldCheck, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useFetch } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { money, imgUrl, waLink } from "../lib/shop";
import { ProductGrid } from "../components/site/ProductCard";
import { Seo } from "../components/site/Seo";

function ColourBadge({ name, hex, small }) {
  return (
    <span className={`flex items-center gap-1.5 bg-black/70 backdrop-blur rounded-full font-bold uppercase tracking-wider ${small ? "text-[9px] px-1.5 py-0.5" : "text-[11px] px-2.5 py-1"}`}>
      <i className={`${small ? "w-2.5 h-2.5" : "w-3.5 h-3.5"} rounded-full border border-white/40`} style={{ background: hex }} />{name}
    </span>
  );
}

function Gallery({ images, name, active, setActive, hexOf, selected }) {
  const [zoom, setZoom] = useState(null);
  const touch = useRef(null);
  const cur = images[active] || images[0];
  const go = (d) => { setZoom(null); setActive((active + d + images.length) % images.length); };
  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setZoom(`${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`);
  };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - (touch.current ?? 0);
    if (Math.abs(dx) > 40 && images.length > 1) go(dx < 0 ? 1 : -1);
  };
  return (
    <div className="lg:sticky lg:top-28">
      <div data-testid="product-main-image" onMouseMove={move} onMouseLeave={() => setZoom(null)} onClick={() => setZoom(zoom ? null : "50% 50%")}
        onTouchStart={(e) => { touch.current = e.touches[0].clientX; }} onTouchEnd={onTouchEnd}
        className="relative aspect-square rounded-2xl overflow-hidden bg-[#18181c] cursor-zoom-in select-none">
        <img key={cur?.url} src={imgUrl(cur?.url)} alt={name} className="w-full h-full object-cover rise" style={{ animationDuration: ".4s", transformOrigin: zoom || "50% 50%", transform: zoom ? "scale(2.2)" : "scale(1)", transition: "transform .3s ease" }} />
        {cur?.colour && (!selected || cur.colour === selected) && <div className="absolute top-3 left-3" data-testid="product-image-colour-badge"><ColourBadge name={cur.colour} hex={hexOf(cur.colour)} /></div>}
        {images.length > 1 && (<>
          <button onClick={(e) => { e.stopPropagation(); go(-1); }} className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 grid place-items-center hover:bg-black/80" aria-label="Previous photo" data-testid="product-gallery-prev"><ChevronLeft size={20} /></button>
          <button onClick={(e) => { e.stopPropagation(); go(1); }} className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 grid place-items-center hover:bg-black/80" aria-label="Next photo" data-testid="product-gallery-next"><ChevronRight size={20} /></button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">{images.map((_, i) => <span key={i} className={`h-1.5 rounded-full ${i === active ? "w-5 bg-[#d4af37]" : "w-1.5 bg-white/50"}`} style={{ transition: "width .3s" }} />)}</div>
        </>)}
        <span className="hidden lg:block absolute bottom-3 right-3 text-[10px] uppercase tracking-widest bg-black/60 px-2 py-1 rounded">Hover to zoom</span>
      </div>
      {images.length > 1 && (
        <div className="flex gap-3 mt-3 overflow-x-auto no-scrollbar">
          {images.map((im, i) => (
            <button key={im.id || i} onClick={() => setActive(i)} data-testid={`product-thumb-${i}`}
              className={`relative shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 ${i === active ? "border-[#d4af37]" : "border-transparent opacity-60 hover:opacity-100"}`}>
              <img src={imgUrl(im.url)} alt={`${name} view ${i + 1}`} className="w-full h-full object-cover" />
              {im.colour && <i className="absolute top-1 right-1 w-3 h-3 rounded-full border border-white/60" style={{ background: hexOf(im.colour) }} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ColourPicker({ colours, colour, pick, soldOut }) {
  return (
    <div>
    <div className="flex flex-wrap gap-2.5" data-testid="product-colour-options">
      {colours.map((c) => (
        <button key={c.name} onClick={() => pick(c.name)} data-testid={`product-colour-${c.name.toLowerCase().replace(/\s+/g, "-")}`} title={c.name} aria-label={c.name}
          className={`relative w-11 h-11 rounded-full border-2 grid place-items-center ${colour === c.name ? "border-[#d4af37] scale-110" : "border-white/15 hover:border-white/50"} ${soldOut(c.name) ? "opacity-40" : ""}`}
          style={{ transition: "transform .2s, border-color .2s" }}>
          <i className="w-8 h-8 rounded-full border border-white/30" style={{ background: c.hex }} />
          {soldOut(c.name) && <span className="absolute w-9 h-0.5 bg-red-400 rotate-45 rounded" />}
        </button>
      ))}
    </div>
    <p className="text-sm mt-3" data-testid="product-selected-colour"><b>{colour}</b>{soldOut(colour) && <span className="text-red-400"> · Sold out</span>}<span className="text-zinc-500"> · {colours.length} colour{colours.length > 1 ? "s" : ""}</span></p>
    </div>
  );
}

function SizePicker({ variants, size, setSize }) {
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="product-size-grid">
      {variants.map((v) => {
        const out = v.stock <= 0;
        return (
          <button key={v.size} disabled={out} onClick={() => setSize(v.size)} data-testid={`product-size-${v.size.toLowerCase().replace(/\s+/g, "-")}`}
            className={`relative min-h-[54px] rounded-xl border text-sm font-bold ${out ? "border-white/5 text-zinc-600 cursor-not-allowed" : size === v.size ? "border-[#d4af37] bg-[#d4af37] text-black" : "border-white/15 hover:border-white/50"}`}
            style={{ transition: "background-color .2s, border-color .2s" }}>
            <span className={out ? "line-through" : ""}>{v.size}</span>
            {out && <span className="block text-[9px] tracking-widest text-red-400/80 no-underline">SOLD OUT</span>}
            {!out && v.stock <= 2 && <span className="block text-[9px] tracking-widest opacity-70">{v.stock} left</span>}
          </button>
        );
      })}
    </div>
  );
}

export default function Product() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { data, loading, error } = useFetch(`/products/${slug}`);
  const { addItem, settings } = useStore();
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(1);
  const [colour, setColour] = useState("");
  const [active, setActive] = useState(0);
  const colours = data?.product?.colours || [];
  useEffect(() => {
    const first = data?.product?.colours?.[0]?.name || "";
    setColour(first); setSize(""); setQty(1);
    const idx = data?.product?.images?.findIndex((im) => im.colour === first);
    setActive(idx > 0 ? idx : 0);
  }, [data]);
  if (loading) return <div className="container-x py-24 text-zinc-400">Loading...</div>;
  if (error || !data) return <div className="container-x py-24"><h1 className="font-display text-5xl">Product not found</h1></div>;
  const p = data.product;
  const images = p.images.length ? p.images : [{ url: "" }];
  const hexOf = (n) => colours.find((c) => c.name === n)?.hex || "#888";
  const shown = colours.length ? p.variants.filter((v) => v.colour === colour) : p.variants;
  const variant = shown.find((v) => v.size === size);
  const pickColour = (c) => {
    if (c === colour) return;
    setColour(c); setSize(""); setQty(1);
    const idx = images.findIndex((im) => im.colour === c);
    if (idx >= 0) setActive(idx); else setActive(0);
  };
  const showImage = (i) => {
    setActive(i);
    const c = images[i]?.colour;
    if (c && c !== colour && colours.some((x) => x.name === c)) { setColour(c); setSize(""); setQty(1); }
  };
  const sale = p.on_sale && p.sale_price && p.sale_price < p.price && !variant?.price;
  const unit = variant?.price || p.effective_price;
  const sku = variant?.sku || p.sku;
  const soldOut = (n) => !p.variants.some((v) => v.colour === n && v.stock > 0);
  const add = (buyNow) => {
    if (colours.length && !colour) return toast.error("Please select a colour first");
    if (!size) return toast.error("Please select your size first");
    const image = images.find((im) => colour && im.colour === colour)?.url || images[0]?.url;
    addItem({ product_id: p.id, slug: p.slug, name: p.name, brand: p.brand, image, size, colour: colours.length ? colour : "", qty, price: unit, sku, max: variant.stock });
    if (buyNow) return nav("/checkout");
    toast.success(`${p.name} (${[colours.length ? colour : "", size].filter(Boolean).join(", ")}) added to cart`, { action: { label: "View cart", onClick: () => nav("/cart") } });
  };
  const url = `${window.location.origin}/products/${p.slug}`;
  const jsonLd = {
    "@context": "https://schema.org", "@type": "Product", name: p.name, brand: { "@type": "Brand", name: p.brand },
    image: p.images.map((i) => imgUrl(i.url).startsWith("http") ? imgUrl(i.url) : window.location.origin + i.url),
    description: p.description, sku: p.sku, color: p.colour,
    offers: { "@type": "Offer", priceCurrency: "ZAR", price: p.effective_price, url, availability: p.in_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock" },
  };
  return (
    <div className="container-x py-8 sm:py-14">
      <Seo title={p.name} description={`${p.name} by ${p.brand} — ${money(p.effective_price)}. ${p.description?.slice(0, 120)}`} image={jsonLd.image[0]} type="product" jsonLd={jsonLd} />
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
        <Gallery images={images} name={p.name} active={active} setActive={showImage} hexOf={hexOf} selected={colour} />
        <div>
          <p className="eyebrow" data-testid="product-brand">{p.brand}</p>
          <h1 className="font-display text-5xl sm:text-7xl leading-[0.9] mt-2" data-testid="product-name">{p.name}</h1>
          <div className="flex items-baseline gap-3 mt-4">
            <span className={`text-3xl font-extrabold ${sale ? "text-gold" : ""}`} data-testid="product-price">{!variant && p.min_price !== p.max_price ? `From ${money(p.min_price)}` : money(unit)}</span>
            {sale && <span className="text-lg text-zinc-500 line-through" data-testid="product-regular-price">{money(p.price)}</span>}
            {sale && <span className="text-xs font-extrabold bg-red-500 px-2 py-1 rounded">SALE</span>}
          </div>
          <p className="text-sm text-zinc-400 mt-2">Colour: <span className="text-white" data-testid="product-colour">{colours.length ? colour : p.colour}</span> · {p.gender} · SKU <span data-testid="product-sku">{sku}</span></p>
          <p className={`text-sm font-bold mt-2 ${p.in_stock ? "text-emerald-400" : "text-red-400"}`} data-testid="product-stock-status">
            {p.in_stock ? (variant ? `${variant.stock} in stock for ${size}` : "In stock — select your size") : "Out of stock"}
          </p>
          {colours.length > 0 && (
            <div className="mt-8">
              <p className="label">Select colour</p>
              <ColourPicker colours={colours} colour={colour} pick={pickColour} soldOut={soldOut} />
            </div>
          )}
          <div className="mt-8">
            <div className="flex justify-between items-center mb-3"><p className="label !mb-0">Select size</p><a href="/faq#sizing" className="text-xs underline text-zinc-400">Size guide</a></div>
            <SizePicker variants={shown} size={size} setSize={(s) => { setSize(s); setQty(1); }} />
            {!shown.length && <p className="text-sm text-zinc-500">No sizes available in this colour.</p>}
          </div>
          <div className="mt-6 flex items-center gap-4">
            <p className="label !mb-0">Qty</p>
            <div className="flex items-center border border-white/15 rounded-full">
              <button className="w-12 h-12 grid place-items-center" onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="product-qty-decrease" aria-label="Decrease"><Minus size={16} /></button>
              <span className="w-8 text-center font-bold" data-testid="product-qty-value">{qty}</span>
              <button className="w-12 h-12 grid place-items-center" onClick={() => setQty((q) => Math.min(variant?.stock || 1, q + 1))} data-testid="product-qty-increase" aria-label="Increase"><Plus size={16} /></button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-8">
            <button disabled={!p.in_stock} onClick={() => add(false)} className="btn-gold" data-testid="add-to-cart-button">Add to Cart</button>
            <button disabled={!p.in_stock} onClick={() => add(true)} className="btn-outline" data-testid="buy-now-button">Buy Now</button>
          </div>
          <a href={waLink(settings?.whatsapp_number || "", `Hi Mwezzy Footwear 👋 I have a question about ${p.name}${size ? ` (${size})` : ""}: ${url}`)}
            target="_blank" rel="noreferrer" data-testid="whatsapp-enquiry-button"
            className="mt-3 w-full min-h-[48px] rounded-full border border-[#25D366]/50 text-[#25D366] font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 hover:bg-[#25D366]/10">
            <MessageCircle size={18} /> Enquire on WhatsApp
          </a>
          <div className="mt-8 grid grid-cols-2 gap-3 text-xs text-zinc-400">
            <div className="card-dark p-4 flex gap-3"><Truck size={18} className="text-gold shrink-0" /> FREE delivery: Uber (Jhb) or PAXI to PEP</div>
            <div className="card-dark p-4 flex gap-3"><ShieldCheck size={18} className="text-gold shrink-0" /> Secure online payment</div>
          </div>
          {p.description && (
            <div className="mt-8 border-t border-white/10 pt-6">
              <p className="label">Description</p>
              <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-line" data-testid="product-description">{p.description}</p>
            </div>
          )}
        </div>
      </div>
      {data.related.length > 0 && (
        <section className="mt-20" data-testid="related-products">
          <h2 className="font-display text-5xl mb-8">You may also like</h2>
          <ProductGrid items={data.related} />
        </section>
      )}
    </div>
  );
}
